import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';

let initiated = false;
bus.on(constants.EVENTS.APP.READY, () => {
  if (initiated) return;
  initiated = true;

  // USSD event listener
  bus.on(
    constants.EVENTS.SHARED.LAUNCHED_USSD,
    async ({ ussdCode, simSubscriptionId, slotIndex }) => {
      await launchUssd(ussdCode, simSubscriptionId, slotIndex);
    }
  );
  bus.on(constants.EVENTS.USSD.REQUEST_SIMS_LIST, async () => {
    const simList = await getSimList();
    bus.emit(constants.EVENTS.USSD.SIMS_LIST_READY, { simList });
  });
});

async function launchUssd(ussdCode, simSubscriptionId, slotIndex) {
  // If not inside a native WebView (window.parent === window or no postMessage bridge),
  // skip the async check entirely to preserve the user-gesture context.
  // On iOS Safari, async delays break programmatic a.click() / window.open().
  const isInNativeFrame = window.parent !== window;

  if (!isInNativeFrame) {
    // Direct tel: scheme — must stay synchronous to keep the user-gesture context on iOS
    openTelLink(ussdCode);
    return;
  }

  // First check if USSD interactive is available (native WebView context)
  const ussdAvailable = await checkUssdInteractive();

  if (ussdAvailable) {
    await launchUssdInteractive(ussdCode, simSubscriptionId, slotIndex);
  } else {
    // Fallback: tel link (still inside native frame, async is fine here)
    openTelLink(ussdCode);
  }
}

function openTelLink(ussdCode) {
  const url = `tel:${ussdCode.replace(/#/g, '%23')}`;
  // window.location.href is the most reliable way to trigger tel: on iOS Safari
  window.location.href = url;
}

async function checkUssdInteractive() {
  return new Promise((resolve) => {
    const requestId = Date.now();
    let resolved = false;

    function handleMessage(event) {
      if (
        event.data?.type === 'CHECK_USSD_INTERACTIF_RESPONSE' &&
        event.data.requestId === requestId
      ) {
        window.removeEventListener('message', handleMessage);
        resolved = true;
        resolve(!!event.data.available);
      }
    }

    window.addEventListener('message', handleMessage);

    // Send check request
    window.parent.postMessage(
      { type: 'CHECK_USSD_INTERACTIF', requestId },
      '*'
    );

    // Timeout after 1s
    setTimeout(() => {
      if (!resolved) {
        window.removeEventListener('message', handleMessage);
        resolve(false);
      }
    }, 1000);
  });
}

async function launchUssdInteractive(
  ussdCode,
  simSubscriptionId = null,
  slotIndex = null
) {
  let launchEventName = 'LAUNCH_USSD_INTERACTIF';
  let launchResponseEventName = 'LAUNCH_USSD_INTERACTIF_RESPONSE';
  if (slotIndex !== null) {
    launchEventName = 'LAUNCH_USSD_INTERACTIF_FOR_SIMSLOT';
    launchResponseEventName = 'LAUNCH_USSD_INTERACTIF_FOR_SIMSLOT_RESPONSE';
  }
  const launchUssdRes = await new Promise((resolve) => {
    const requestId = Date.now();
    function handleMessage(event) {
      console.log('🚀 ~ handleMessage ~ event:', event);
      if (
        event.data?.type === launchResponseEventName &&
        event.data.requestId === requestId
      ) {
        window.removeEventListener('message', handleMessage);
        resolve(event.data);
      }
    }
    window.addEventListener('message', handleMessage);
    const payload = { type: launchEventName, requestId, ussdCode };
    if (slotIndex !== null) {
      payload.slotIndex = slotIndex;
    }
    window.parent.postMessage(payload, '*');
  });
  return launchUssdRes;
}

// Expected response format: simList
// [
//   {
//     slotIndex: 0,
//     carrierName: "MTN",
//     displayName: "MTN",
//     subscriptionId: 1
//   },
//   ...
// ]
async function getSimList() {
  const ussdAvailable = await checkUssdInteractive();
  if (ussdAvailable) {
    const getSimsRes = await new Promise((resolve) => {
      const requestId = Date.now();
      function handleMessage(event) {
        console.log('🚀 ~ handleMessage ~ event:', event);
        if (
          event.data?.type === 'GET_TEL_SIM_LIST_RESPONSE' &&
          event.data.requestId === requestId
        ) {
          window.removeEventListener('message', handleMessage);
          resolve(event.data);
        }
      }
      window.addEventListener('message', handleMessage);
      window.parent.postMessage({ type: 'GET_TEL_SIM_LIST', requestId }, '*');
    });
    return getSimsRes?.simList || [];
  }
  return [];
}
